import { TextControl, isEmail, isSuccess } from "@dgs/core";
import axios from "axios";
import { useField, useFormikContext } from "formik";
import React, { FocusEvent, ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";

export interface TextFieldProps {
	prefix?: ReactNode;
	suffix?: ReactNode;
	label?: ReactNode;
	infoText?: ReactNode;
	name: string;
	placeholder?: string;
	description?: string;
	maxChars?: number;
	disabled?: boolean;
	readOnly?: boolean;
	type?: "color" | "date" | "number" | "password" | "search" | "tel" | "text" | "time";
	min?: string;
	max?: string;
	step?: number;
	autoFocus?: boolean;
	className?: string;
	autoComplete?: string;
	required?: boolean;
	nonNullable?: boolean;
	onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
}

const divideProps = ({
	autoFocus = false,
	disabled = false,
	readOnly = false,
	label,
	infoText,
	max,
	min,
	step,
	name,
	placeholder,
	description,
	maxChars,
	type,
	prefix,
	suffix,
	className,
	autoComplete,
	required,
}: TextFieldProps) => {
	return {
		controlProps: {
			label,
			infoText,
			placeholder,
			description,
			maxChars,
			disabled,
			readOnly,
			type,
			autoFocus,
			min,
			max,
			step,
			prefix,
			suffix,
			className,
			autoComplete,
			required,
		},
		useFieldProps: {
			name,
		},
	};
};

export const RegistrationEmailDataField = React.forwardRef(function RegistrationEmailDataField(
	props: TextFieldProps,
	ref: React.Ref<HTMLInputElement>,
) {
	const { t } = useTranslation();
	const { controlProps, useFieldProps } = divideProps(props);
	const { isSubmitting } = useFormikContext();

	const validateEmail = useCallback(
		async (email: string) => {
			if (!email || !isEmail(email)) return;
			const res = await axios.post("validate-email", { email }, { skipErrorHandler: true });
			if (!isSuccess(res) && res.status === 422) {
				return t("The email must be a valid email address.");
			}
			return;
		},
		[t],
	);

	const [field, meta] = useField({ name: useFieldProps.name, validate: validateEmail });

	return (
		<TextControl
			ref={ref}
			{...controlProps}
			{...field}
			disabled={controlProps.disabled || isSubmitting}
			error={meta.touched && meta.error ? meta.error : undefined}
			value={field.value ?? ""}
		/>
	);
});
